import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import { Textarea } from "src/components/base/textarea";

/**
 * The properties for {@link ChangeTagDescDialog}
 */
export type ChangeTagDescDialogProps = {
    /** Callback that should be called when closing the popup */
    onClose: () => void;

    /** Callback that should be called when the form has been submitted successfully */
    onSubmit: () => void;

    /** The tag to rename */
    tag: SimpleTag;
};

/**
 * A dialog to rename a device
 */
export default function ChangeTagDescDialog(props: ChangeTagDescDialogProps) {
    const [t] = useTranslation("dialog-change-tag-desc");

    const form = useForm({
        defaultValues: { description: props.tag.description },
        onSubmit: async ({ value }) => {
            await ApiInternal.tags.setDescription(props.tag.uuid, value.description);
            props.onSubmit();
        },
    });

    return (
        <Dialog open={true} onClose={() => props.onClose()}>
            <DialogTitle>{t("heading.change-tag-desc", { tag: props.tag.name })}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.new-desc")}</RequiredLabel>
                                        <Textarea
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            resizable={false}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <PrimaryButton type={"submit"}>{t("button.change-tag-desc")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
